import { DownOutlined, LockOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { TransferTargetOption } from "../../../types/index.type";
import { useOptionalTransfer } from "../../TransferButton/TransferContext";
import TransferTargetMenu from "../../TransferButton/TransferTargetMenu";
import {
  EMPTY_ENTRY,
  type TargetEntry,
  effectiveOption,
  isSplitEnabled,
  noDefaultCopy,
  optionLabel,
  unavailableCopy,
} from "../../TransferButton/transferTarget";

// What the order card needs from its Encounter field.
export interface CardEncounter {
  // The field renders (one-click EHR, schedule read). Otherwise the card is
  // today's card exactly and its actions send no appointment.
  active: boolean;
  // Where the note and orders go: the clinician's choice, else the suggestion.
  option: TransferTargetOption | null;
  // Ready, but nothing to default to (two visits that day, none near the
  // recording): the actions wait for a choice, like an unchosen catalogue pick.
  needsChoice: boolean;
  // One quiet line for the card's hint row, or "".
  hint: string;
  entry: TargetEntry;
}

/**
 * The order card's Encounter field state for one note. Reads the same target
 * as the note-page Transfer button (TransferProvider), so the two can never
 * name different appointments. `audioId` null = no field (chat-staged card, a
 * note already transferred, no provider).
 */
export const useCardEncounter = (audioId: number | null | undefined): CardEncounter => {
  const transfer = useOptionalTransfer();
  const ensureTarget = transfer?.ensureTarget;
  const releaseTarget = transfer?.releaseTarget;
  useEffect(() => {
    if (!audioId) return;
    ensureTarget?.(audioId);
    return () => releaseTarget?.(audioId);
  }, [audioId, ensureTarget, releaseTarget]);

  const entry = audioId && transfer ? transfer.getTargetForAudio(audioId) : EMPTY_ENTRY;
  const target = entry.target;
  const none: CardEncounter = { active: false, option: null, needsChoice: false, hint: "", entry };
  if (!audioId || !isSplitEnabled(entry) || !target || entry.slow) return none;
  if (target.state === "unavailable")
    return { ...none, hint: `${unavailableCopy(target.reason)} You'll pick the appointment on the way.` };
  if (target.state === "empty")
    return { ...none, hint: "No appointments found for this patient — you'll pick one on the way." };
  if (target.state !== "ready" || !target.options.length) return { ...none, active: target.state === "loading", entry };
  const option = effectiveOption(target);
  return {
    active: true,
    option,
    needsChoice: !option,
    hint: option ? "" : noDefaultCopy(target) || "Choose the encounter for this note.",
    entry,
  };
};

interface EncounterFieldProps {
  audioId: number;
  field: CardEncounter;
  // After the click: the encounter the note/orders were sent to, read-only.
  lockedLabel?: string;
  disabled?: boolean;
}

/**
 * "Encounter" — a form field on the Superdoc order card, directly above its
 * actions. Prefilled with the suggestion (the encounter already compiled for
 * this note); opening it shows the note button's schedule menu. Picking a row
 * only sets the field — nothing starts until a button is clicked — and the
 * note-page button follows the same choice.
 */
const EncounterField: React.FC<EncounterFieldProps> = ({ audioId, field, lockedLabel, disabled }) => {
  const transfer = useOptionalTransfer();
  const [open, setOpen] = useState(false);
  // Frozen while open, so a background refresh never reorders rows under the
  // cursor (same rule as the note button's menu).
  const frozenRef = useRef<TargetEntry | null>(null);

  if (lockedLabel) {
    return (
      <div style={styles.wrap}>
        <span style={styles.label}>Encounter</span>
        <div style={{ ...styles.control, ...styles.locked }} aria-readonly>
          <span style={styles.value}>{lockedLabel}</span>
          <LockOutlined style={{ color: "#9CA3AF", fontSize: 12 }} />
        </div>
      </div>
    );
  }
  if (!field.active || !transfer) return null;

  const loading = field.entry.target?.state !== "ready";
  if (loading) {
    return (
      <div style={styles.wrap}>
        <span style={styles.label}>Encounter</span>
        <div style={{ ...styles.control, ...styles.placeholderBox }} role="status" aria-label="Reading the schedule">
          <span style={styles.shimmer} />
        </div>
      </div>
    );
  }

  const option = field.option;
  const details = option ? [option.visit_type, option.provider].filter(Boolean).join(" · ") : "";
  const menuEntry = open && frozenRef.current ? frozenRef.current : field.entry;

  return (
    <div style={styles.wrap}>
      <span style={styles.label} id={`encounter-label-${audioId}`}>
        Encounter
      </span>
      <Dropdown
        open={open}
        onOpenChange={(next) => {
          frozenRef.current = next ? field.entry : null;
          setOpen(next);
        }}
        trigger={["click"]}
        placement="topLeft"
        disabled={disabled}
        dropdownRender={() => (
          <TransferTargetMenu
            entry={menuEntry}
            width={372}
            selectedId={option?.id ?? null}
            onPick={(picked) => {
              setOpen(false);
              frozenRef.current = null;
              void transfer.pickTarget(audioId, picked.id);
            }}
            onRefresh={() => {
              frozenRef.current = null;
              transfer.refreshTarget(audioId, true);
            }}
          />
        )}
      >
        <button
          type="button"
          aria-labelledby={`encounter-label-${audioId}`}
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={disabled}
          style={{
            ...styles.control,
            ...(option ? {} : styles.needsChoice),
            ...(disabled ? styles.disabled : {}),
          }}
        >
          {option ? (
            <>
              <span style={styles.value}>
                <strong style={{ color: "#111827" }}>{optionLabel(option)}</strong>
                {details && <span style={styles.details}> · {details}</span>}
              </span>
              {option.transferred_note_id ? <span style={styles.tag}>Has a note</span> : null}
              {option.signed ? <span style={styles.tag}>Signed</span> : null}
              {option.compile === "pending" ? <span style={styles.preparing}>Preparing…</span> : null}
            </>
          ) : (
            <span style={styles.value}>Choose an encounter</span>
          )}
          <DownOutlined style={{ fontSize: 10, color: "#6B7280" }} />
        </button>
      </Dropdown>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: "4px" },
  label: { fontSize: "12px", fontWeight: 500, color: "#6B7280" },
  control: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    minHeight: "36px",
    padding: "7px 11px",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    background: "#FFFFFF",
    fontSize: "13px",
    color: "#374151",
    textAlign: "left",
    cursor: "pointer",
  },
  value: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  details: { color: "#6B7280" },
  needsChoice: { borderColor: "#F59E0B", background: "#FFFBEB", color: "#B45309" },
  locked: { background: "#F9FAFB", color: "#6B7280", cursor: "default" },
  disabled: { opacity: 0.55, cursor: "not-allowed" },
  placeholderBox: { cursor: "default" },
  shimmer: { display: "inline-block", width: "60%", height: "10px", borderRadius: "5px", background: "#E5E7EB" },
  tag: {
    flexShrink: 0,
    fontSize: "11px",
    padding: "1px 6px",
    borderRadius: "999px",
    background: "#F3F4F6",
    color: "#4B5563",
  },
  preparing: { flexShrink: 0, fontSize: "11px", fontStyle: "italic", color: "#6B7280" },
};

export default EncounterField;
